import React, { Component } from 'react';
import { Alert, Badge, Button, ProgressBar, Table } from 'react-bootstrap';
import InfoTooltip from 'components/controls/InfoTooltip';
import CatalogGroups from 'components/catalog/catalogGroups';
import Highlight, { hitSpans } from 'components/controls/highlight';
import LimitsFilterBar from 'components/profile/limitsFilterBar';
import { formatCatalogValue } from 'context/catalogUnits';
import { SHOWS, filterLimits } from 'context/limitsFilter';
import { orderedEntries, sourcePill, typeEnabled } from 'context/limitsCatalog';

const READS = { user: 'usersIdLimitsGet', org: 'orgsIdLimitsGet', blob: 'blobsIdLimitsGet' };

const requestKey = (target, ids) => `${target}:${(ids || []).join('/')}`;

/** A served limits group as a CatalogGroups card; `filter` ({ terms, rows }) narrows it while a filter is active. */
export function limitsGroup(group, collapsible, filter) {
  const enabled = typeEnabled(group);
  const off = collapsible && false === enabled;
  const terms = filter ? filter.terms : [];
  const pill = off ? <Badge variant="secondary" className="title-tag-badge-muted">Not enabled</Badge> : null;
  const entries = orderedEntries(group);
  return {
    key: group.group,
    label: group.label,
    title: filter ? <Highlight text={group.label} terms={terms} /> : undefined,
    caption: undefined === enabled ? null : <code><Highlight text={group.group} terms={terms} /></code>,
    note: filter
      ? <><span className="small text-muted">{filter.rows.length} of {entries.length}</span>
        {pill && <span className="ml-2">{pill}</span>}</>
      : pill,
    collapsed: off && !filter,
    entries: filter ? filter.rows : entries,
  };
}

function LimitValue({ item, terms }) {
  const modules = 'modules' === item.unit && Array.isArray(item.value) && item.value.length > 0;
  const overridden = !item.fixed && 'default' in item && JSON.stringify(item.default) !== JSON.stringify(item.value);
  return (
    <>
      {modules
        ? item.value.map((name, index) => (
          <code key={index} className="mr-2"><Highlight text={String(name)} terms={terms} /></code>
        ))
        : <Highlight text={formatCatalogValue(item.unit, item.value)} terms={terms} />}
      {overridden && <div className="small text-muted">default {formatCatalogValue(item.unit, item.default)}</div>}
    </>
  );
}

function LimitHeld({ item }) {
  if (!('held' in item) || null === item.held) return null;
  const held = formatCatalogValue(item.unit, item.held);
  if ('number' !== typeof item.value || 'number' !== typeof item.held) {
    return <div className="small text-muted">{held} held</div>;
  }
  const percent = item.value > 0 ? Math.min(100, (100 * item.held) / item.value) : 100;
  return (
    <>
      <ProgressBar now={percent} variant={item.held >= item.value ? 'warning' : undefined}
        className="bh-catalog-meter mb-1" />
      <div className="small text-muted">{held} of {formatCatalogValue(item.unit, item.value)}</div>
    </>
  );
}

/** One limit: its label and id, its value, its holding, and the pill saying who set it. */
export function LimitRow({ item, terms = [] }) {
  const pill = sourcePill(item.source);
  const described = item.description && hitSpans(String(item.description), terms).length > 0;
  return (
    <tr>
      <td className="w-50">
        <Highlight text={item.label || item.id} terms={terms} />
        {item.description && <span className={described ? 'ml-1 bh-hit-info' : 'ml-1'}>
          <InfoTooltip text={item.description} /></span>}
        <div><code><Highlight text={item.id} terms={terms} /></code></div>
      </td>
      <td className="w-25"><LimitValue item={item} terms={terms} /></td>
      <td className="w-25"><LimitHeld item={item} /></td>
      <td className="text-right text-nowrap">
        {item.fixed
          ? <Badge variant="secondary" className="title-tag-badge-muted">Fixed</Badge>
          : pill && <Badge variant="secondary" className={pill.className}>{pill.label}</Badge>}
      </td>
    </tr>
  );
}

const renderLimitRows = (entries, terms) => (
  <Table size="sm" className="mb-0">
    <tbody>
      {entries.map((item, index) => <LimitRow key={`${item.id}:${index}`} item={item} terms={terms} />)}
    </tbody>
  </Table>
);

/** The section's message when the filter leaves nothing, with the button that undoes it. */
export function NoMatches({ query, show, onReset }) {
  const noun = (SHOWS.find((option) => option.key === show) || SHOWS[0]).noun;
  const text = query.trim();
  return (
    <div className="bh-filter-empty">
      <div className="text-break">
        {text ? <>No {noun} match <strong>“{text}”</strong>.</> : <>No {noun} on this page.</>}
      </div>
      <Button variant="light" size="sm" className="mt-2" onClick={onReset}>Clear filter</Button>
    </div>
  );
}

export default class Limits extends Component {
  constructor(props) {
    super(props);
    this.state = { groups: null, loading: true, error: null, query: '', show: 'all' };
    this.filterInput = React.createRef();
    this.onKeyDown = this.onKeyDown.bind(this);
    this.resetFilter = this.resetFilter.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
    document.addEventListener('keydown', this.onKeyDown);
    this.loadLimits();
  }

  componentDidUpdate(prevProps) {
    const before = requestKey(prevProps.target, prevProps.targetIds);
    if (before !== requestKey(this.props.target, this.props.targetIds)) this.loadLimits();
  }

  componentWillUnmount() {
    this.mounted = false;
    document.removeEventListener('keydown', this.onKeyDown);
  }

  // "/" focuses the filter from anywhere on the page, unless the reader is already typing somewhere. The pane stays
  // mounted while hidden, so a hidden field or an open modal must leave the key to the browser.
  onKeyDown(event) {
    if ('/' !== event.key || event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented) return;
    const target = event.target;
    const typing = target && (target.isContentEditable
      || (target.closest && target.closest('input, textarea, select')));
    const field = this.filterInput.current;
    if (typing || !field || document.body.classList.contains('modal-open')) return;
    if (field.checkVisibility && !field.checkVisibility()) return;
    event.preventDefault();
    field.focus();
    field.select();
  }

  resetFilter() {
    this.setState({ query: '', show: 'all' });
    if (this.filterInput.current) this.filterInput.current.focus();
  }

  loadLimits() {
    const { app, target, targetIds } = this.props;
    const read = READS[target];
    if (!read) {
      this.setState({ groups: null, loading: false, error: `No limits are served for "${target}".` });
      return;
    }
    const key = requestKey(target, targetIds);
    this.requested = key;
    this.setState({ groups: null, loading: true, error: null, query: '', show: 'all' });
    app.api[read](...(targetIds || [])).then((response) => {
      if (!this.mounted || key !== this.requested) return;
      const groups = response && response.data && response.data.groups;
      this.setState({ groups: Array.isArray(groups) ? groups : [], loading: false });
    }, (error) => {
      if (!this.mounted || key !== this.requested) return;
      this.setState({ loading: false, error: (error && error.message) || 'Unexpected error occurred' });
    });
  }

  render() {
    const { groups, loading, error, query, show } = this.state;
    const collapsible = 'blob' !== this.props.target;
    const served = !loading && groups ? groups.filter((group) => group && 'object' === typeof group) : null;
    const filter = served ? filterLimits(served, query, show) : null;
    return (
      <>
        <h5>Limits</h5>
        <hr/>
        {loading && <Alert variant="primary">Loading limits...</Alert>}
        {!loading && error && <Alert variant="warning">Could not load limits: {error}</Alert>}
        {served && served.length > 0 &&
          <LimitsFilterBar query={query} show={show} counts={filter.counts} total={filter.total}
            filtering={filter.filtering} inputRef={this.filterInput}
            onQuery={(value) => this.setState({ query: value })} onShow={(key) => this.setState({ show: key })} />
        }
        {served && filter.filtering && 0 === filter.counts[show] &&
          <NoMatches query={query} show={show} onReset={this.resetFilter} />
        }
        {served && (
          <CatalogGroups
            groups={filter.filtering && 0 === filter.counts[show] ? [] : filter.matches.map(({ group, rows }) =>
              limitsGroup(group, collapsible, filter.filtering ? { terms: filter.terms, rows } : undefined))}
            renderEntries={(entries) => renderLimitRows(entries, filter.terms)}
          />
        )}
      </>
    );
  }
}
